import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Save, X, Edit2, Undo2 } from 'lucide-react';
import GoogleMap from './GoogleMap';

/**
 * Reusable Festival Form component for creating and editing festivals
 *
 * @param {Object} props
 * @param {Object} props.initialData - Initial form data (for editing)
 * @param {Function} props.onSubmit - Called with form data when submitted
 * @param {Function} props.onCancel - Called when cancel button is clicked
 * @param {boolean} props.isLoading - Whether the form is submitting
 * @param {string} props.submitLabel - Label for submit button (default: "Save Festival")
 * @param {boolean} props.showStatus - Whether to show status field (default: true)
 * @param {boolean} props.readOnly - Whether to start in read-only mode (default: false)
 * @param {boolean} props.showEditButton - Whether to show the Edit button in read-only mode (default: true)
 */
const FestivalForm = ({
  initialData = {},
  onSubmit,
  onCancel,
  isLoading = false,
  submitLabel = 'Save Festival',
  showStatus = true,
  readOnly = false,
  showEditButton = true
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    location_name: '',
    location_lat: null,
    location_lng: null,
    zoom_level: 15,
    status: 'draft',
    ...initialData
  });

  // Track whether we're in editing mode (starts as true unless readOnly is set)
  const [isEditing, setIsEditing] = useState(!readOnly);

  // Store the original values when entering edit mode (for change detection and undo)
  const originalValuesRef = useRef(null);
  const [savedMapState, setSavedMapState] = useState(null);

  // Update form when initialData changes (for edit mode)
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData(prev => ({
        ...prev,
        ...initialData
      }));
    }
  }, [initialData]);

  // Save original values when entering edit mode
  const handleEnterEditMode = () => {
    originalValuesRef.current = { ...formData };
    setSavedMapState({
      location_lat: formData.location_lat,
      location_lng: formData.location_lng,
      zoom_level: formData.zoom_level
    });
    setIsEditing(true);
  };

  // Check if a field has changed from original
  const hasFieldChanged = (fieldName) => {
    if (!originalValuesRef.current || !isEditing) return false;
    const original = originalValuesRef.current[fieldName];
    const current = formData[fieldName];
    // Handle null/undefined/empty string comparisons
    if ((original === null || original === undefined || original === '') &&
        (current === null || current === undefined || current === '')) {
      return false;
    }
    return original !== current;
  };

  // Check if map location has changed (use parseFloat for consistent comparison)
  const hasMapChanged = () => {
    if (!savedMapState) return false;
    const savedLat = savedMapState.location_lat !== null ? parseFloat(savedMapState.location_lat) : null;
    const savedLng = savedMapState.location_lng !== null ? parseFloat(savedMapState.location_lng) : null;
    const savedZoom = savedMapState.zoom_level !== null ? parseInt(savedMapState.zoom_level) : null;
    const currentLat = formData.location_lat !== null ? parseFloat(formData.location_lat) : null;
    const currentLng = formData.location_lng !== null ? parseFloat(formData.location_lng) : null;
    const currentZoom = formData.zoom_level !== null ? parseInt(formData.zoom_level) : null;
    return savedLat !== currentLat || savedLng !== currentLng || savedZoom !== currentZoom;
  };

  // Generate a key for the map to force re-render when undo is clicked
  const [mapKey, setMapKey] = useState(0);

  // Undo map changes
  const handleUndoMap = () => {
    if (savedMapState) {
      setFormData(prev => ({
        ...prev,
        location_lat: savedMapState.location_lat,
        location_lng: savedMapState.location_lng,
        zoom_level: savedMapState.zoom_level
      }));
      // Force map to re-render with original position
      setMapKey(prev => prev + 1);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLocationSelect = useCallback((lat, lng, zoom) => {
    setFormData(prev => ({
      ...prev,
      location_lat: lat,
      location_lng: lng,
      zoom_level: zoom
    }));
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Convert string coordinates to numbers and handle empty dates
    const submitData = {
      ...formData,
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
      location_lat: formData.location_lat ? parseFloat(formData.location_lat) : null,
      location_lng: formData.location_lng ? parseFloat(formData.location_lng) : null,
      zoom_level: parseInt(formData.zoom_level) || 15
    };

    onSubmit(submitData);
    // If in read-only mode context, return to read-only after save
    if (readOnly) {
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    // Reset form data to initial data
    setFormData({
      name: '',
      description: '',
      start_date: '',
      end_date: '',
      location_name: '',
      location_lat: null,
      location_lng: null,
      zoom_level: 15,
      status: 'draft',
      ...initialData
    });
    originalValuesRef.current = null;
    setSavedMapState(null);
    setIsEditing(false);
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  // Get status label
  const getStatusLabel = (status) => {
    const labels = { draft: 'Draft', published: 'Published', archived: 'Archived' };
    return labels[status] || status;
  };

  // Get input style with optional changed highlight
  const getInputStyle = (fieldName) => ({
    width: '100%',
    padding: '12px',
    border: hasFieldChanged(fieldName) ? '2px solid #90EE90' : '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px'
  });



  const readOnlyStyle = {
    padding: '12px',
    background: '#f8f9fa',
    borderRadius: '4px',
    fontSize: '14px'
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '30px' }}>
      {/* Basic Information */}
      <div style={{
        background: 'white',
        padding: '30px',
        borderRadius: '8px',
        border: '1px solid #e0e0e0'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
            Basic Information
          </h2>
          {/* Edit button (read-only mode) or Save/Cancel buttons (editing mode) */}
          <div style={{ display: 'flex', gap: '10px' }}>
            {readOnly && showEditButton && !isEditing && (
              <button
                type="button"
                onClick={handleEnterEditMode}
                style={{
                  padding: '8px 16px',
                  background: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Edit2 size={16} />
                Edit
              </button>
            )}
            {isEditing && (
              <>
                <button
                  type="button"
                  onClick={readOnly ? handleCancelEdit : onCancel}
                  style={{
                    padding: '8px 16px',
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <X size={16} />
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    padding: '8px 16px',
                    background: isLoading ? '#ccc' : '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Save size={16} />
                  {isLoading ? 'Saving...' : submitLabel}
                </button>
              </>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gap: '20px' }}>
          <div className="form-group">
            <label>Festival Name {isEditing ? '*' : ''}</label>
            {isEditing ? (
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                style={getInputStyle('name')}
              />
            ) : (
              <div style={readOnlyStyle}>{formData.name || 'No name set'}</div>
            )}
          </div>

          <div className="form-group">
            <label>Description</label>
            {isEditing ? (
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="4"
                style={{ ...getInputStyle('description'), resize: 'vertical' }}
              />
            ) : (
              <div style={{ ...readOnlyStyle, whiteSpace: 'pre-wrap' }}>
                {formData.description || 'No description set'}
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div className="form-group">
              <label>Start Date & Time</label>
              {isEditing ? (
                <input
                  type="datetime-local"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                  style={getInputStyle('start_date')}
                />
              ) : (
                <div style={readOnlyStyle}>{formatDate(formData.start_date)}</div>
              )}
            </div>

            <div className="form-group">
              <label>End Date & Time</label>
              {isEditing ? (
                <input
                  type="datetime-local"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleInputChange}
                  style={getInputStyle('end_date')}
                />
              ) : (
                <div style={readOnlyStyle}>{formatDate(formData.end_date)}</div>
              )}
            </div>
          </div>

          {showStatus && (
            <div className="form-group">
              <label>Status</label>
              {isEditing ? (
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  style={getInputStyle('status')}
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              ) : (
                <div style={readOnlyStyle}>{getStatusLabel(formData.status)}</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Location Information - show in both view and edit mode */}
      <div style={{
        background: 'white',
        padding: '30px',
        borderRadius: '8px',
        border: '1px solid #e0e0e0'
      }}>
        <h2 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: '600' }}>
          Location Information
        </h2>

        <div className="form-group" style={{ marginBottom: '20px' }}>
          <label>Location Name</label>
          {isEditing ? (
            <input
              type="text"
              name="location_name"
              value={formData.location_name}
              onChange={handleInputChange}
              placeholder="e.g., Central Park, New York"
              style={getInputStyle('location_name')}
            />
          ) : (
            <div style={readOnlyStyle}>{formData.location_name || 'No location name set'}</div>
          )}
        </div>

        <div className="form-group">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <label style={{ margin: 0 }}>Event Area</label>
            {isEditing && hasMapChanged() && (
              <button
                type="button"
                onClick={handleUndoMap}
                style={{
                  padding: '4px 8px',
                  background: '#f8f9fa',
                  color: '#666',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title="Undo map changes"
              >
                <Undo2 size={14} />
                Undo
              </button>
            )}
          </div>
          {isEditing && (
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
              Move the map so it includes the area where the event is taking place.
            </p>
          )}
          <div style={{
            maxWidth: '800px',
            border: (isEditing && hasMapChanged()) ? '2px solid #90EE90' : '1px solid #ddd',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <GoogleMap
              key={`${mapKey}-${isEditing}`}
              onLocationSelect={isEditing ? handleLocationSelect : undefined}
              initialLat={formData.location_lat}
              initialLng={formData.location_lng}
              initialZoom={formData.zoom_level}
              height="800px"
              showSearch={false}
              readOnly={!isEditing}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default FestivalForm;

